import React from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Selectors } from 'Reducers'
import { formatFreeSubscriptionUntil, getUserLanguage } from 'Lib'

/**
 * Confirmation for the Urban Sports Club voucher flow, shown on the download screen
 * once the code has been redeemed.
 *
 * Split in two so it can bracket the screen's title the way the reviewed wireframe
 * does — check mark above, granted window below.
 *
 * The window comes from `users/{uid}/freeSubscription`, which the backend wrote
 * during redemption: 'active' for an unlimited grant, otherwise a 'yyyy-MM-dd'
 * expiry. When it has not synced yet we confirm without a date rather than
 * inventing one.
 */

const CheckWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const CheckCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.basicWhite};
  font-size: ${({ theme }) => theme.fontSizes.large};
`

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

const Badge = styled.span`
  background-color: ${({ theme }) => theme.colors.green100};
  color: ${({ theme }) => theme.colors.green900};
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: 600;
  padding: ${({ theme }) => theme.spacing.xsmall} ${({ theme }) => theme.spacing.mediumSmall};
`

export const UscActivatedCheck: React.FC = () => (
  <CheckWrapper data-testid='usc-premium-activated'>
    <CheckCircle aria-hidden='true'>✓</CheckCircle>
  </CheckWrapper>
)

export const UscActivatedBadge: React.FC = () => {
  const { t } = useTranslation()
  const user = useSelector(Selectors.getUser)
  const freeSubscription = user?.freeSubscription

  const activeUntil = freeSubscription && freeSubscription !== 'active' && freeSubscription !== 'available'
    ? formatFreeSubscriptionUntil(freeSubscription, getUserLanguage())
    : null

  if (activeUntil) {
    return (
      <BadgeWrapper>
        <Badge data-testid='usc-active-until'>{t('usc.activated.activeUntil', { date: activeUntil })}</Badge>
      </BadgeWrapper>
    )
  }

  if (freeSubscription === 'active') {
    return (
      <BadgeWrapper>
        <Badge data-testid='usc-active-unlimited'>{t('usc.activated.unlimited')}</Badge>
      </BadgeWrapper>
    )
  }

  return null
}
